import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import en from "./en";
import fr from "./fr";

const DICTS = { en, fr };
export const LANGS = ["it", "en", "fr"];
const Ctx = createContext({ lang: "it", setLang: () => {} });

const initialLang = () => {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (LANGS.includes(fromUrl)) return fromUrl;
  const saved = localStorage.getItem("lang");
  return LANGS.includes(saved) ? saved : "it";
};

// Keep ?lang= in the address so every language has its own URL (Italian = no parameter).
const syncUrl = (lang) => {
  const url = new URL(window.location.href);
  if (lang === "it") url.searchParams.delete("lang");
  else url.searchParams.set("lang", lang);
  if (url.href !== window.location.href) window.history.replaceState(window.history.state, "", url);
};

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(initialLang);
  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    syncUrl(lang);
  }, [lang]);
  return <Ctx.Provider value={{ lang, setLang }}>{children}</Ctx.Provider>;
};

export const useLang = () => useContext(Ctx);

// Re-applies ?lang= after in-site navigation (links do not carry the parameter). Render inside the Router.
export const LangUrlSync = () => {
  const { lang } = useContext(Ctx);
  const { pathname, search, hash } = useLocation();
  useEffect(() => { syncUrl(lang); }, [lang, pathname, search, hash]);
  return null;
};

export const useT = () => {
  const { lang } = useContext(Ctx);
  return useCallback((key, it) => (lang === "it" ? it : DICTS[lang]?.[key] ?? it), [lang]);
};

export const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-white/15 p-1 ${className}`} data-testid="lang-switch">
      {LANGS.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          className={`tap rounded-full px-3 py-2 font-mono text-xs uppercase tracking-[0.12em] transition-[background-color,color] duration-300 ${lang === l ? "bg-[#F7F5F0] text-[#121110]" : "text-[#F7F5F0] hover:bg-white/10"}`}
          aria-pressed={lang === l}
          data-testid={`lang-${l}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
